import { useState, useEffect } from "react";
import axios from "axios";
import React from "react";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { ChevronRightIcon, XMarkIcon } from "@heroicons/react/20/solid";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const money0 = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const money2 = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const moneyShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}k`;
  return `$${v}`;
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    minHeight: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "input:focus": { boxShadow: "none" },
  }),
  singleValue: (provided) => ({ ...provided, color: "#e5e7eb" }),
  placeholder: (provided) => ({ ...provided, color: "#6b7280" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { color: "#f97316" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#374151" : "#1f2937",
    color: state.isSelected ? "#fb923c" : "#e5e7eb",
    "&:active": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "0.5rem",
    overflow: "hidden",
    zIndex: 30,
  }),
};

const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors disabled:cursor-not-allowed disabled:opacity-50";

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors";

const goalInputClass =
  "w-20 rounded-lg bg-gray-900 border border-gray-700 px-2 py-1.5 text-center text-sm text-gray-100 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none";

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  red: {
    wrap: "bg-red-500/10 text-red-400 ring-red-500/20",
    dot: "fill-red-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  gray: {
    wrap: "bg-gray-500/10 text-gray-300 ring-gray-500/20",
    dot: "fill-gray-400",
  },
};

function Badge({ tone = "gray", children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      <svg
        viewBox="0 0 6 6"
        aria-hidden="true"
        className={classNames("size-1.5", t.dot)}
      >
        <circle r={3} cx={3} cy={3} />
      </svg>
      {children}
    </span>
  );
}

const statusTone = (s) =>
  s === "Won"
    ? "green"
    : s === "Lost"
      ? "red"
      : s === "No Opportunity"
        ? "gray"
        : "yellow";

const ACCENTS = {
  blue: "bg-blue-500",
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  orange: "bg-orange-500",
};

function Stat({
  label,
  value,
  sub,
  accent = "blue",
  valueClass = "text-gray-100",
}) {
  return (
    <div className="relative overflow-hidden rounded-xl bg-gray-800 p-4 pl-5 ring-1 ring-white/10">
      <span
        className={classNames("absolute inset-y-0 left-0 w-1", ACCENTS[accent])}
      />
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className={classNames("mt-1 text-2xl font-bold", valueClass)}>
        {value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

function Block({ title, subtitle, action, children, className = "" }) {
  return (
    <section className={className}>
      <div className="mb-3 flex items-end justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-orange-400">
          {title}
          {subtitle && (
            <span className="ml-2 text-xs font-normal normal-case tracking-normal text-gray-500">
              {subtitle}
            </span>
          )}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Card({ title, action, children, className = "" }) {
  return (
    <div
      className={classNames(
        "rounded-xl bg-gray-800 ring-1 ring-white/10",
        className,
      )}
    >
      {title && (
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg bg-gray-900 px-3 py-2 shadow-xl ring-1 ring-white/10">
        {label && (
          <p className="mb-1 text-sm font-semibold text-gray-100">{label}</p>
        )}
        {payload.map((entry, index) => (
          <p key={index} className="text-sm text-gray-300">
            <span
              className="mr-2 inline-block size-2 rounded-full"
              style={{ backgroundColor: entry.color || entry.payload?.fill }}
            />
            {entry.name ? `${entry.name}: ` : ""}
            {money2(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const getClosingRateColor = (actual, goal) => {
  if (!actual) return "text-gray-500";
  if (actual >= goal) return "text-green-400";
  if (actual >= goal * 0.75) return "text-yellow-400";
  return "text-red-400";
};

const getSubmissionGoalColor = (actual, goal) => {
  if (!goal) return "text-gray-300";
  if (actual >= goal) return "text-green-400";
  if (actual >= goal * 0.75) return "text-yellow-400";
  return "text-red-400";
};

const COLORS = [
  "#F97316",
  "#3B82F6",
  "#EF4444",
  "#10B981",
  "#8B5CF6",
  "#F59E0B",
  "#EC4899",
];

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

const thBase =
  "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-400";

export default function OpportunitiesAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState("year");
  const [breakdownYear, setBreakdownYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [expandedBands, setExpandedBands] = useState({});
  const [goals, setGoals] = useState([]); // [{ band, closingRateGoal, quarterlySubmissionGoals }]
  const [goalsModalOpen, setGoalsModalOpen] = useState(false);
  const [editingGoals, setEditingGoals] = useState([]);
  const [goalsSaving, setGoalsSaving] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const closeAlert = () => setAlert((prev) => ({ ...prev, open: false }));
  const showAlert = (type, title, message) =>
    setAlert({ open: true, type, title, message, onConfirm: closeAlert });

  const getTimeRangeOptions = () => {
    const base = [
      { value: "current", label: "All Time" },
      { value: "year", label: `This Year (${new Date().getFullYear()})` },
      { value: "month", label: "This Month" },
    ];

    if (analytics?.allAvailableYears) {
      const currentYear = new Date().getFullYear();
      analytics.allAvailableYears
        .filter((y) => y < currentYear)
        .sort((a, b) => b - a)
        .forEach((year) => {
          base.push({ value: String(year), label: String(year) });
        });
    }

    return base;
  };

  const toggleBandExpanded = (bandLabel) => {
    setExpandedBands((prev) => ({
      ...prev,
      [bandLabel]: !prev[bandLabel],
    }));
  };

  // Total estimated $ value for a band
  const getBandEstimatedValue = (band) => {
    if (band.opportunities && band.opportunities.length > 0) {
      return band.opportunities.reduce(
        (sum, o) => sum + (o.estimatedRevenue || 0),
        0,
      );
    }
    return band.totals?.estimatedValue || 0;
  };

  // Which quarter an opportunity's proposal was sent in
  const getOppQuarter = (opp) => {
    if (!opp.proposalSentDate) return null;
    const month = new Date(opp.proposalSentDate).getMonth();
    if (month < 3) return "Q1";
    if (month < 6) return "Q2";
    if (month < 9) return "Q3";
    return "Q4";
  };

  // Estimated $ value for one band + quarter
  const getQuarterEstimatedValue = (band, quarterLabel) => {
    if (band.opportunities && band.opportunities.length > 0) {
      return band.opportunities
        .filter((o) => getOppQuarter(o) === quarterLabel)
        .reduce((sum, o) => sum + (o.estimatedRevenue || 0), 0);
    }
    const q = band.quarters?.find((qq) => qq.quarter === quarterLabel);
    return q?.estimatedValue || 0;
  };

  const fetchAnalytics = async (
    overrideTimeRange,
    overrideBreakdownYear,
    { silentReload } = {},
  ) => {
    if (!silentReload) setLoading(true);
    if (silentReload) setBreakdownLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const currentTimeRange = overrideTimeRange || timeRange;
      const bdYear =
        overrideBreakdownYear !== undefined
          ? overrideBreakdownYear
          : breakdownYear;

      let url = `${API}/opportunities/analytics?timeRange=${currentTimeRange}`;
      if (bdYear) {
        url += `&breakdownYear=${bdYear}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch analytics");
      }

      setAnalytics(response.data.analytics);
    } catch (err) {
      console.error("Analytics fetch error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch analytics. Please try again.",
      );
    } finally {
      setLoading(false);
      setBreakdownLoading(false);
    }
  };

  const fetchGoals = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/company-profile/opportunity-goals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGoals(res.data || []);
    } catch (err) {
      console.error("Failed to fetch goals", err);
    }
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  const getGoalForBand = (bandLabel) =>
    goals.find((g) => g.band === bandLabel) || null;

  const saveGoals = async () => {
    setGoalsSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/company-profile/opportunity-goals`,
        { goals: editingGoals },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setGoals(editingGoals);
      setGoalsModalOpen(false);
      // Re-fetch analytics so the closing rate goal column reflects saved values
      fetchAnalytics();
      showAlert("success", "Success", "Goals saved successfully!");
    } catch (err) {
      console.error("Failed to save goals", err);
      showAlert(
        "error",
        "Error",
        "Failed to save goals: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setGoalsSaving(false);
    }
  };

  const openGoalsModal = () => {
    if (!analytics?.quarterlyBreakdown) return;
    // Pre-populate from saved goals; fall back to what analytics already has
    const seeded = analytics.quarterlyBreakdown.map((band) => {
      const saved = getGoalForBand(band.band);
      return {
        band: band.band,
        closingRateGoal: saved?.closingRateGoal ?? band.closingRateGoal ?? 0,
        quarterlySubmissionGoals: {
          Q1: saved?.quarterlySubmissionGoals?.Q1 ?? 0,
          Q2: saved?.quarterlySubmissionGoals?.Q2 ?? 0,
          Q3: saved?.quarterlySubmissionGoals?.Q3 ?? 0,
          Q4: saved?.quarterlySubmissionGoals?.Q4 ?? 0,
        },
      };
    });
    setEditingGoals(seeded);
    setGoalsModalOpen(true);
  };

  // Fetch when timeRange changes (breakdownYear is preserved)
  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  // Collapse expanded bands only when timeRange changes
  useEffect(() => {
    setExpandedBands({});
  }, [timeRange]);

  const handleTimeRangeChange = (selected) => {
    setTimeRange(selected ? selected.value : "year");
  };

  const handleBreakdownYearChange = (year) => {
    setExpandedBands({});
    setBreakdownYear(year);
    fetchAnalytics(undefined, year, { silentReload: true });
  };

  const getBidHistoryBarChartData = () => {
    if (!analytics || !analytics.monthlyBidHistory) return [];
    return analytics.monthlyBidHistory.map((item) => ({
      month: item.month,
      "Submitted Value": item.totalValue || 0,
      "Won Value": item.wonValue || 0,
      "Lost Value": item.lostValue || 0,
    }));
  };

  const getAccountsPieChartData = () => {
    if (!analytics || !analytics.winsByClient) return [];
    return analytics.winsByClient.map((item) => ({
      name: item.clientName,
      value: item.totalWonValue,
    }));
  };

  const timeRangeOptions = getTimeRangeOptions();
  const sp = analytics?.submissionPerformance;
  const ap = analytics?.awardPerformance;
  const lp = analytics?.livePipeline;
  const coverage = lp?.pipelineCoverageRatio || 0;
  const bidHistoryData = getBidHistoryBarChartData();
  const pieData = getAccountsPieChartData();

  return (
    <div className="rounded-2xl bg-gray-900 p-4 shadow-xl ring-1 ring-white/10 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">
            Pipeline
          </p>
          <h2 className="text-2xl font-bold text-white">
            Opportunity Analytics
          </h2>
        </div>
        <div className="w-full sm:w-56">
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
            Time Range
          </label>
          <Select
            options={timeRangeOptions}
            value={timeRangeOptions.find((o) => o.value === timeRange) || null}
            onChange={handleTimeRangeChange}
            placeholder="Select time range"
            isSearchable={false}
            styles={customSelectStyles}
          />
        </div>
      </div>

      {loading && (
        <div className="py-16 text-center">
          <div className="inline-block size-8 animate-spin rounded-full border-b-2 border-orange-500" />
          <p className="mt-3 text-sm text-gray-400">Loading analytics...</p>
        </div>
      )}

      {error && (
        <div className="mb-6 flex items-center justify-between gap-3 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-500/20">
          <span>{error}</span>
          <button
            onClick={() => fetchAnalytics()}
            className="font-semibold text-orange-400 hover:text-orange-300"
          >
            Retry
          </button>
        </div>
      )}

      {analytics && !loading && (
        <div className="space-y-8">
          {/* Submission performance */}
          <Block
            title="Submission Performance"
            subtitle="by proposal sent date"
          >
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat
                label="Submitted"
                value={sp?.submitted || 0}
                accent="blue"
              />
              <Stat label="Won" value={sp?.won || 0} accent="blue" />
              <Stat label="Lost" value={sp?.lost || 0} accent="blue" />
              <Stat
                label="Win Rate"
                value={`${sp?.winRate || 0}%`}
                accent="blue"
              />
              <Stat
                label="Submitted Value"
                value={money0(sp?.submittedValue)}
                accent="blue"
              />
              <Stat
                label="Won Value"
                value={money0(sp?.wonValue)}
                accent="blue"
              />
              <Stat
                label="Lost Value"
                value={money0(sp?.lostValue)}
                accent="blue"
              />
              <Stat label="Avg Bid" value={money0(sp?.avgBid)} accent="blue" />
            </div>
          </Block>

          {/* Award performance */}
          <Block title="Award Performance" subtitle="by award date">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
              <Stat
                label="Awards Secured"
                value={ap?.awardsSecured || 0}
                accent="green"
              />
              <Stat
                label="Award Value"
                value={money0(ap?.awardValue)}
                accent="green"
              />
              <Stat
                label="Avg Award"
                value={money0(ap?.avgAward)}
                accent="green"
              />
              <Stat
                label="Backlog Added"
                value={money0(ap?.backlogAdded)}
                accent="green"
              />
              <Stat
                label={`% Toward Target (${moneyShort(ap?.annualTarget)})`}
                value={`${ap?.pctTowardTarget || 0}%`}
                accent="green"
              />
              <Stat
                label="Large Jobs Won ($3M+)"
                value={ap?.largeJobs || 0}
                sub={ap?.largeJobsValue ? money0(ap.largeJobsValue) : null}
                accent="green"
              />
            </div>
          </Block>

          {/* Live pipeline */}
          <Block title="Live Pipeline" subtitle="all pending submissions">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat
                label="Pending Count"
                value={lp?.pendingCount || 0}
                accent="yellow"
              />
              <Stat
                label="Pending Value"
                value={money0(lp?.pendingValue)}
                accent="yellow"
              />
              <Stat
                label="Weighted Pipeline (30%)"
                value={money0(lp?.weightedPipeline)}
                accent="yellow"
              />
              <Stat
                label="Avg Size"
                value={money0(lp?.avgSize)}
                accent="yellow"
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {(lp?.byYear || []).map((yr) => (
                <Stat
                  key={yr.year}
                  label={`${yr.year} Pending`}
                  value={yr.count}
                  sub={`${moneyShort(yr.value)} total`}
                  accent="yellow"
                />
              ))}
              <Stat
                label="Pipeline Coverage"
                value={`${coverage}x`}
                sub={`vs ${moneyShort(lp?.revenueTarget)} target`}
                accent="orange"
                valueClass={
                  coverage >= 1.5
                    ? "text-green-400"
                    : coverage >= 1.0
                      ? "text-yellow-400"
                      : "text-red-400"
                }
              />
            </div>
          </Block>

          {/* Monthly bid history */}
          {bidHistoryData.length > 0 && (
            <Card title="Monthly Bid History">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={bidHistoryData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    stroke="#9ca3af"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={moneyShort}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#d1d5db" }} />
                  <Bar
                    dataKey="Submitted Value"
                    fill="#3B82F6"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="Won Value"
                    fill="#10B981"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="Lost Value"
                    fill="#EF4444"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Quarterly breakdown (own year selector) */}
          {analytics.quarterlyBreakdown &&
            analytics.quarterlyBreakdown.length > 0 && (
              <Card
                title={`Quarterly Breakdown by Bid Amount${
                  analytics.selectedBreakdownYear
                    ? ` — ${analytics.selectedBreakdownYear}`
                    : ""
                }`}
                action={
                  <button onClick={openGoalsModal} className={btnSecondary}>
                    Edit Goals
                  </button>
                }
              >
                {analytics.availableYears &&
                  analytics.availableYears.length > 0 && (
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        Year
                      </span>
                      <div className="flex flex-wrap gap-1 rounded-lg bg-gray-900 p-1 ring-1 ring-white/10">
                        {analytics.availableYears.map((year) => (
                          <button
                            key={year}
                            onClick={() => handleBreakdownYearChange(year)}
                            className={classNames(
                              "rounded-md px-3 py-1 text-sm font-semibold transition-colors",
                              analytics.selectedBreakdownYear === year
                                ? "bg-orange-500 text-white shadow"
                                : "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
                            )}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                <div
                  className={classNames(
                    "overflow-x-auto rounded-lg ring-1 ring-white/10 transition-opacity duration-200",
                    breakdownLoading
                      ? "pointer-events-none opacity-50"
                      : "opacity-100",
                  )}
                >
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-gray-900">
                      <tr>
                        <th
                          rowSpan={2}
                          className="border-r border-white/10 px-4 py-3 text-left align-bottom text-xs font-semibold uppercase tracking-wide text-gray-400"
                        >
                          Band
                        </th>
                        <th
                          rowSpan={2}
                          className="border-r border-white/10 px-4 py-3 text-center align-bottom text-xs font-semibold uppercase tracking-wide text-gray-400"
                        >
                          Close Goal
                        </th>
                        {QUARTERS.map((q) => (
                          <th
                            key={q}
                            colSpan={3}
                            className={classNames(
                              thBase,
                              "border-r border-white/10",
                            )}
                          >
                            {q}
                          </th>
                        ))}
                        <th
                          colSpan={3}
                          className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-orange-400"
                        >
                          Year Total
                        </th>
                      </tr>
                      <tr>
                        {QUARTERS.map((q) => (
                          <React.Fragment key={`${q}-sub`}>
                            <th className={classNames(thBase, "normal-case")}>
                              # Sub
                            </th>
                            <th className={classNames(thBase, "normal-case")}>
                              Close %
                            </th>
                            <th
                              className={classNames(
                                thBase,
                                "border-r border-white/10 normal-case",
                              )}
                            >
                              Est Rev.
                            </th>
                          </React.Fragment>
                        ))}
                        <th className="px-3 py-2 text-center text-xs font-semibold text-orange-400">
                          # Sub
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-orange-400">
                          Close %
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-orange-400">
                          Est Rev.
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {analytics.quarterlyBreakdown.map((band) => {
                        const isExpanded = expandedBands[band.band];
                        const hasOpportunities =
                          band.opportunities && band.opportunities.length > 0;

                        return (
                          <React.Fragment key={band.band}>
                            <tr
                              className={classNames(
                                "transition-colors",
                                hasOpportunities
                                  ? "cursor-pointer hover:bg-gray-700/50"
                                  : "",
                                isExpanded ? "bg-gray-700/40" : "",
                              )}
                              onClick={() =>
                                hasOpportunities &&
                                toggleBandExpanded(band.band)
                              }
                            >
                              <td className="whitespace-nowrap border-r border-white/10 px-4 py-3 text-sm font-semibold text-gray-100">
                                <div className="flex items-center gap-2">
                                  {hasOpportunities ? (
                                    <ChevronRightIcon
                                      className={classNames(
                                        "size-4 text-gray-400 transition-transform duration-200",
                                        isExpanded
                                          ? "rotate-90 text-orange-400"
                                          : "",
                                      )}
                                    />
                                  ) : (
                                    <span className="w-4" />
                                  )}
                                  {band.band}
                                </div>
                              </td>
                              <td className="whitespace-nowrap border-r border-white/10 px-4 py-3 text-center text-sm text-gray-300">
                                {band.closingRateGoal}%
                              </td>
                              {band.quarters.map((q) => {
                                const goal = getGoalForBand(band.band);
                                const qGoal =
                                  goal?.quarterlySubmissionGoals?.[q.quarter] ||
                                  0;
                                return (
                                  <React.Fragment key={q.quarter}>
                                    <td className="whitespace-nowrap px-3 py-3 text-center text-sm">
                                      <span
                                        className={getSubmissionGoalColor(
                                          q.estimates,
                                          qGoal,
                                        )}
                                      >
                                        {q.estimates}
                                        {qGoal > 0 && (
                                          <span className="ml-1 text-xs text-gray-500">
                                            /{qGoal}
                                          </span>
                                        )}
                                      </span>
                                    </td>
                                    <td
                                      className={classNames(
                                        "whitespace-nowrap px-3 py-3 text-center text-sm font-medium",
                                        getClosingRateColor(
                                          q.closingRate,
                                          band.closingRateGoal,
                                        ),
                                      )}
                                    >
                                      {q.closingRate > 0
                                        ? `${q.closingRate}%`
                                        : "-"}
                                    </td>
                                    <td className="whitespace-nowrap border-r border-white/10 px-3 py-3 text-center text-sm text-gray-300">
                                      {money0(
                                        getQuarterEstimatedValue(
                                          band,
                                          q.quarter,
                                        ),
                                      )}
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                              <td className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-100">
                                {band.totals.estimates}
                              </td>
                              <td
                                className={classNames(
                                  "whitespace-nowrap px-3 py-3 text-center text-sm font-bold",
                                  getClosingRateColor(
                                    band.totals.closingRate,
                                    band.closingRateGoal,
                                  ),
                                )}
                              >
                                {band.totals.closingRate > 0
                                  ? `${band.totals.closingRate}%`
                                  : "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-center text-sm font-bold text-gray-100">
                                {money0(getBandEstimatedValue(band))}
                              </td>
                            </tr>

                            {/* Expanded drilldown */}
                            {isExpanded && hasOpportunities && (
                              <tr>
                                <td colSpan={17} className="p-0">
                                  <div className="border-l-2 border-orange-500 bg-gray-900/70">
                                    <div className="px-6 py-4">
                                      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                        <h4 className="text-sm font-semibold text-orange-400">
                                          {band.band} —{" "}
                                          {band.opportunities.length} submitted
                                          bid
                                          {band.opportunities.length === 1
                                            ? ""
                                            : "s"}
                                        </h4>
                                        <div className="flex items-center gap-4 text-xs text-gray-400">
                                          <span className="flex items-center gap-1.5">
                                            <span className="size-2 rounded-full bg-green-400" />
                                            Won: {band.totals.won}
                                          </span>
                                          <span className="flex items-center gap-1.5">
                                            <span className="size-2 rounded-full bg-red-400" />
                                            Lost: {band.totals.lost}
                                          </span>
                                          <span className="flex items-center gap-1.5">
                                            <span className="size-2 rounded-full bg-yellow-400" />
                                            Pending:{" "}
                                            {band.totals.estimates -
                                              band.totals.won -
                                              band.totals.lost}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full">
                                          <thead>
                                            <tr className="border-b border-white/10">
                                              {[
                                                ["Title", "text-left"],
                                                ["Account", "text-left"],
                                                ["Estimator", "text-left"],
                                                ["Est. Revenue", "text-right"],
                                                ["Status", "text-center"],
                                                ["Quarter", "text-center"],
                                                ["Submitted", "text-center"],
                                              ].map(([h, align]) => (
                                                <th
                                                  key={h}
                                                  className={classNames(
                                                    "px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400",
                                                    align,
                                                  )}
                                                >
                                                  {h}
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-white/5">
                                            {band.opportunities.map((opp) => (
                                              <tr
                                                key={opp._id}
                                                className="transition-colors hover:bg-gray-700/40"
                                              >
                                                <td className="max-w-xs truncate px-3 py-2 text-sm">
                                                  <a
                                                    href={`/opportunities/${opp._id}`}
                                                    className="text-orange-400 hover:text-orange-300 hover:underline"
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                  >
                                                    {opp.title}
                                                  </a>
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-300">
                                                  {opp.account?.companyName ||
                                                    "-"}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-gray-300">
                                                  {opp.estimator?.displayName ||
                                                    "-"}
                                                </td>
                                                <td className="px-3 py-2 text-right text-sm font-medium text-gray-100">
                                                  {money2(opp.estimatedRevenue)}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                  <Badge
                                                    tone={statusTone(
                                                      opp.status,
                                                    )}
                                                  >
                                                    {opp.status}
                                                  </Badge>
                                                </td>
                                                <td className="px-3 py-2 text-center text-sm text-gray-300">
                                                  {getOppQuarter(opp) || "-"}
                                                </td>
                                                <td className="px-3 py-2 text-center text-sm text-gray-400">
                                                  {opp.proposalSentDate
                                                    ? new Date(
                                                        opp.proposalSentDate,
                                                      ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                          month: "short",
                                                          day: "numeric",
                                                        },
                                                      )
                                                    : "-"}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                          <tfoot>
                                            <tr className="border-t border-white/10">
                                              <td
                                                colSpan={3}
                                                className="px-3 py-2 text-sm font-semibold text-gray-300"
                                              >
                                                Band Total
                                              </td>
                                              <td className="px-3 py-2 text-right text-sm font-bold text-gray-100">
                                                {money2(
                                                  band.opportunities.reduce(
                                                    (sum, o) =>
                                                      sum +
                                                      (o.estimatedRevenue || 0),
                                                    0,
                                                  ),
                                                )}
                                              </td>
                                              <td
                                                colSpan={3}
                                                className="px-3 py-2 text-center text-sm text-gray-400"
                                              >
                                                {band.totals.estimates}{" "}
                                                submitted · {band.totals.won}{" "}
                                                won · {band.totals.lost} lost
                                              </td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}

                      {/* Grand totals */}
                      <tr className="bg-gray-900 font-semibold">
                        <td className="whitespace-nowrap border-r border-white/10 px-4 py-3 text-sm text-gray-100">
                          <div className="flex items-center gap-2">
                            <span className="w-4" />
                            Total
                          </div>
                        </td>
                        <td className="border-r border-white/10 px-4 py-3" />
                        {[0, 1, 2, 3].map((qi) => {
                          const qLabel = QUARTERS[qi];
                          const qEstimates =
                            analytics.quarterlyBreakdown.reduce(
                              (sum, band) =>
                                sum + (band.quarters[qi]?.estimates || 0),
                              0,
                            );
                          const qWon = analytics.quarterlyBreakdown.reduce(
                            (sum, band) => sum + (band.quarters[qi]?.won || 0),
                            0,
                          );
                          const qLost = analytics.quarterlyBreakdown.reduce(
                            (sum, band) => sum + (band.quarters[qi]?.lost || 0),
                            0,
                          );
                          const qDecided = qWon + qLost;
                          const qRate =
                            qDecided > 0
                              ? Number(((qWon / qDecided) * 100).toFixed(2))
                              : 0;
                          const qEstValue = analytics.quarterlyBreakdown.reduce(
                            (sum, band) =>
                              sum + getQuarterEstimatedValue(band, qLabel),
                            0,
                          );
                          return (
                            <React.Fragment key={`total-q${qi}`}>
                              <td className="whitespace-nowrap px-3 py-3 text-center text-sm text-gray-100">
                                {qEstimates}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-center text-sm text-gray-100">
                                {qRate > 0 ? `${qRate}%` : "-"}
                              </td>
                              <td className="whitespace-nowrap border-r border-white/10 px-3 py-3 text-center text-sm font-bold text-gray-100">
                                {money0(qEstValue)}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        {(() => {
                          const grandEstimates =
                            analytics.quarterlyBreakdown.reduce(
                              (sum, band) => sum + band.totals.estimates,
                              0,
                            );
                          const grandWon = analytics.quarterlyBreakdown.reduce(
                            (sum, band) => sum + band.totals.won,
                            0,
                          );
                          const grandLost = analytics.quarterlyBreakdown.reduce(
                            (sum, band) => sum + band.totals.lost,
                            0,
                          );
                          const grandDecided = grandWon + grandLost;
                          const grandRate =
                            grandDecided > 0
                              ? Number(
                                  ((grandWon / grandDecided) * 100).toFixed(2),
                                )
                              : 0;
                          return (
                            <>
                              <td className="whitespace-nowrap px-3 py-3 text-center text-sm font-bold text-orange-400">
                                {grandEstimates}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-center text-sm font-bold text-orange-400">
                                {grandRate > 0 ? `${grandRate}%` : "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-center text-sm font-bold text-orange-400">
                                {money0(
                                  analytics.quarterlyBreakdown.reduce(
                                    (sum, band) =>
                                      sum + getBandEstimatedValue(band),
                                    0,
                                  ),
                                )}
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-full bg-green-400" />
                    At or above goal
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-full bg-yellow-400" />
                    Within 75% of goal
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-full bg-red-400" />
                    Below 75% of goal
                  </span>
                  <span className="ml-auto italic text-gray-500">
                    Click a row to expand opportunities
                  </span>
                </div>
              </Card>
            )}

          {/* Wins by client */}
          {analytics.winsByClient && analytics.winsByClient.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <Card title="Wins by Client">
                <div className="overflow-x-auto rounded-lg ring-1 ring-white/10">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-gray-900">
                      <tr>
                        {["Client", "Total Won (#)", "Total Won Value"].map(
                          (h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
                            >
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {analytics.winsByClient.map((client, index) => (
                        <tr
                          key={index}
                          className="transition-colors hover:bg-gray-700/40"
                        >
                          <td className="px-4 py-3 text-sm text-gray-100">
                            <span
                              className="mr-2 inline-block size-2 rounded-full"
                              style={{
                                backgroundColor: COLORS[index % COLORS.length],
                              }}
                            />
                            {client.clientName}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                            {client.totalWon}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-100">
                            {money2(client.totalWonValue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-900">
                      <tr className="font-semibold">
                        <td className="px-4 py-3 text-sm text-gray-100">
                          Total
                        </td>
                        <td className="px-4 py-3 text-sm text-orange-400">
                          {analytics.winsByClient.reduce(
                            (sum, client) => sum + (client.totalWon || 0),
                            0,
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-orange-400">
                          {money2(
                            analytics.winsByClient.reduce(
                              (sum, client) =>
                                sum + (client.totalWonValue || 0),
                              0,
                            ),
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>

              <Card title="Win Values by Account">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={380}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="45%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={3}
                        dataKey="value"
                        stroke="#1f2937"
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        wrapperStyle={{ fontSize: 12, color: "#d1d5db" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="py-10 text-center text-sm text-gray-400">
                    No account win data available
                  </p>
                )}
              </Card>
            </div>
          ) : (
            <div className="rounded-xl bg-gray-800 px-6 py-10 text-center text-sm text-gray-400 ring-1 ring-white/10">
              No account win data available
            </div>
          )}
        </div>
      )}

      {/* Goals modal */}
      {goalsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div
            className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-gray-800 shadow-2xl ring-1 ring-white/10"
            role="dialog"
            aria-modal="true"
            aria-label="Edit Opportunity Goals"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <h3 className="text-lg font-semibold text-white">
                Edit Opportunity Goals
              </h3>
              <button
                type="button"
                onClick={() => setGoalsModalOpen(false)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-100"
                aria-label="Close"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>

            <div className="overflow-auto p-5">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Band
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Close Rate %
                    </th>
                    {QUARTERS.map((q) => (
                      <th
                        key={q}
                        className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-400"
                      >
                        {q} Subs
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {editingGoals.map((g, i) => (
                    <tr key={g.band}>
                      <td className="whitespace-nowrap px-3 py-2 text-sm font-semibold text-gray-100">
                        {g.band}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={g.closingRateGoal}
                          onChange={(e) => {
                            const updated = [...editingGoals];
                            updated[i] = {
                              ...updated[i],
                              closingRateGoal: parseFloat(e.target.value) || 0,
                            };
                            setEditingGoals(updated);
                          }}
                          className={goalInputClass}
                        />
                      </td>
                      {QUARTERS.map((q) => (
                        <td key={q} className="px-3 py-2 text-center">
                          <input
                            type="number"
                            min="0"
                            value={g.quarterlySubmissionGoals[q]}
                            onChange={(e) => {
                              const updated = [...editingGoals];
                              updated[i] = {
                                ...updated[i],
                                quarterlySubmissionGoals: {
                                  ...updated[i].quarterlySubmissionGoals,
                                  [q]: parseInt(e.target.value, 10) || 0,
                                },
                              };
                              setEditingGoals(updated);
                            }}
                            className={goalInputClass}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 border-t border-white/10 px-5 py-4">
              <button
                onClick={() => setGoalsModalOpen(false)}
                className={btnSecondary}
              >
                Cancel
              </button>
              <button
                onClick={saveGoals}
                disabled={goalsSaving}
                className={btnPrimary}
              >
                {goalsSaving ? "Saving..." : "Save Goals"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
        onConfirm={alert.onConfirm}
      />
    </div>
  );
}
